// Partner cockpit — cross-tenant ticket inbox (B5B-PARTNER-COCKPIT §3). A single DataTable aggregating
// open tickets across every tenant in the consultant's mandate (bounded ≤50, B4-MCP-CONTRACT), with an
// open/all toggle. Scoped server-side to the mandate. Behind real auth.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { cockpit, CockpitError, type InboxTicket } from "../lib/cockpit";
import { humanize } from "../lib/format";
import { PageHeader } from "@/components/page-header";
import { DataTable, type Column } from "@/components/data-table";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

function dueLabel(due: string | null): { text: string; overdue: boolean } {
  if (!due) return { text: "—", overdue: false };
  const ms = new Date(due).getTime() - Date.now();
  const days = Math.round(ms / 86_400_000);
  if (days < 0) return { text: `OVERDUE ${-days}d`, overdue: true };
  if (days === 0) return { text: "due today", overdue: false };
  return { text: `in ${days}d`, overdue: false };
}

const COLUMNS: Column<InboxTicket>[] = [
  { key: "title", header: "Title", render: (r) => <span className="font-medium">{r.title}</span> },
  { key: "tenant", header: "Tenant", render: (r) => <Badge variant="outline">{r.tenantName}</Badge> },
  { key: "status", header: "Status", render: (r) => <Badge variant="secondary">{r.state ? humanize(r.state) : "—"}</Badge> },
  { key: "priority", header: "Priority", render: (r) => r.priority ?? "—" },
  {
    key: "due", header: "Due", render: (r) => {
      const d = dueLabel(r.dueAt);
      return <span className={d.overdue ? "text-destructive font-medium" : "text-muted-foreground"}>{d.text}</span>;
    },
  },
];

export function InboxRoute() {
  const [status, setStatus] = useState<"open" | "all">("open");
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["cockpit-inbox", status],
    queryFn: () => cockpit.inbox({ status }),
    retry: false,
  });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Inbox" description="Tickets across every tenant in your mandate (max 50)." />

      {error instanceof CockpitError && error.status === 401 ? (
        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">Sign in as a firm consultant to view your cross-tenant inbox.</CardContent></Card>
      ) : error ? (
        <Card><CardContent className="py-8 text-center text-sm text-destructive">{(error as Error).message}</CardContent></Card>
      ) : (
        <>
          <div className="flex gap-2">
            <Button size="sm" variant={status === "open" ? "default" : "outline"} onClick={() => setStatus("open")}>Open</Button>
            <Button size="sm" variant={status === "all" ? "default" : "outline"} onClick={() => setStatus("all")}>All</Button>
          </div>
          <DataTable<InboxTicket>
            columns={COLUMNS}
            rows={data ?? []}
            loading={isLoading}
            getRowId={(r) => r.id}
            onRowClick={(r) => navigate({ to: "/ticket/$ticketId", params: { ticketId: r.id } })}
            empty="No tickets in your mandate."
          />
        </>
      )}
    </div>
  );
}
